import React from "react";
import { Grid, ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails, Tooltip, Button } from "@material-ui/core";
import { Info } from "@material-ui/icons";
import numberFormatter from "utils/numberFormatter";
import styled from "styled-components";
import _ from "lodash";

export const AlignedGrid = styled(Grid)`
  & {
    padding-right:32px;
    padding-bottom: 4px;    
  }
`;
export const RightAlignedDiv = styled.div`
  text-align:right;
`;
const CustomGridItem = styled(Grid)`
  &&&{
    padding:8px;
  }
`;
const AlterGridItem = styled(Grid)`
  &&& {
    background:#f5f5f5;
    padding:8px;
  }
`;

const CenteringGridItem = styled(Grid)`
  &&& {
    display:flex;
    align-items:center;
  }  
`;
export const BoldLabel = styled.div`
  & {
    font-weight:bold;
    font-size:17px;
  }
`;
export const IndentedBoldLabel = styled(BoldLabel)`
  & {
    margin-left:20px;
  }
`;
const CustomInfo = styled(Info)`
  &&&& {
    padding-left: 4px;
    fill: #2196F3;
  }
`;
export const ContentText = styled.div`
  & {
    height:100%    
    font-size:17px;
    text-align:right;
  }
`;
export const RedContentText = styled.div`
  & {
    color: red;
    font-weight: bold;
  }
`;

export const QuoteComparisonExpansionSummary = styled(ExpansionPanelSummary)`
 &&&& { 
   min-height:${props => (props.expanded ? "0" : "30px")};
 }
 &&&& div:first-child {
   margin: 0;
 }
`;

export const QuoteComparisonExpansionPanelDetails = styled(ExpansionPanelDetails)`
 &&&& {
   padding-bottom:0;
   padding-top:0;
 }
 && div:first-child {
   padding-bottom:0;
 }
`;

export const createLoanGridLabel = (label, childElement) => (<CenteringGridItem item xs={2}>
  <BoldLabel>{label}</BoldLabel>
  {childElement}
                                                             </CenteringGridItem>);
export const createIndentedLoanGridLabel = (label, childElement) => (<CenteringGridItem item xs={2}>
  <IndentedBoldLabel>{label}</IndentedBoldLabel>
  {childElement}
                                                                     </CenteringGridItem>);

export const createTooltipLoanGridLabel = (label, tooltipText) => (
  <CenteringGridItem item xs={2}>
    <BoldLabel>{label}</BoldLabel>
    <Tooltip title={tooltipText}><CustomInfo /></Tooltip>
  </CenteringGridItem>);

export const QuoteComparisonExpansionPanel = styled(ExpansionPanel)`
  border-bottom: 1px solid #ccc;
  &&& {
    margin: 0;
  }
`;
export const QuoteComparisonExpansionPanelNoBorder = styled(ExpansionPanel)`
  &&& {
    margin: 0;
  }
`;
export const createLoanGridElements = (loans, property, placeholder, isCombined) => {
  let gridCol;
  if (loans.length <= 2) {
    gridCol = 4;
  } else if (loans.length >= 6) {
    gridCol = 1;
  } else if (loans.length >= 3) {
    gridCol = 2;
  } else {
    gridCol = 3;
  }

  return loans.map((x, i) => {
    if (x.isExistingFinancing || x.originalLoan) {
      return (<AlterGridItem item xs={gridCol} key={`AlterGridItem${placeholder}+${i}`}><ContentText key={`ContentText${placeholder}+${i}`}>{x[property] || (<RightAlignedDiv>{placeholder}</RightAlignedDiv>)}</ContentText></AlterGridItem>);
    }

    return (<CustomGridItem item xs={gridCol} key={`CustomGridItem${placeholder}+${i}`}><ContentText key={`ContentText${placeholder}+${i}`}>{x[property] || (<RightAlignedDiv>{placeholder}</RightAlignedDiv>)}</ContentText></CustomGridItem>);
  });
};

const renderMultiLineString = property => (loan, placeholder) => {
  const value = _.get(loan, property);
  if (!value && value !== 0) {
    return (<RightAlignedDiv>{placeholder}</RightAlignedDiv>);
  }

  return value.split("|").map((text, index) => (<React.Fragment key={`${property}-${index}`}>
    {text}
    <br />
  </React.Fragment>));
};
const renderRate = (property, bustedProperty) => (loan, placeholder) => {
  const value = _.get(loan, property);
  const isBusted = _.get(loan, bustedProperty);
  if (!value && value !== 0) {
    return (<RightAlignedDiv>{placeholder}</RightAlignedDiv>);
  }
  const formatted = numberFormatter.formatRate(value, 2);

  return isBusted !== undefined && isBusted ? (<RedContentText>{formatted}</RedContentText>) : formatted;
};
const renderMoney = property => (loan, placeholder) => {
  const value = _.get(loan, property);
  if (!value && value !== 0) {
    return (<RightAlignedDiv>{placeholder}</RightAlignedDiv>);
  }

  return numberFormatter.formatMoney(value, 0);
};
const renderDSCR = (property, bustedProperty) => (loan, placeholder) => {
  const value = _.get(loan, property);
  const isBusted = _.get(loan, bustedProperty);
  if (!value && value !== 0) {
    return (<RightAlignedDiv>{placeholder}</RightAlignedDiv>);
  }
  const formatted = `${numberFormatter.formatDecimal(value, 2)}x`;

  return isBusted !== undefined && isBusted ? (<RedContentText>{formatted}</RedContentText>) : formatted;
};
const renderString = property => (loan, placeholder) => {
  const value = _.get(loan, property);
  if (!value && value !== 0) {
    return (<RightAlignedDiv>{placeholder}</RightAlignedDiv>);
  }

  return value;
};
const renderAbbreviatedMoney = (property, lessThan100kOverride) => (loan, placeholder) => {
  const value = _.get(loan, property);
  if (!value && value !== 0) {
    return (<RightAlignedDiv>{placeholder}</RightAlignedDiv>);
  }

  return numberFormatter.maskCurrency(loan[property], "\u2014", lessThan100kOverride);
};
const renderPeriod = property => (loan, placeholder) => {
  const value = _.get(loan, property);
  if (!value) {
    return (<RightAlignedDiv>{placeholder}</RightAlignedDiv>);
  }
  if (value / 12 < 1) {
    return `${value} Months`;
  }

  return `${(value / 12).toFixed(0)} Years`;
};
export const createMultiLineStringLoanGridElements = (loans, property, isCombined) => createLoanGridElementsWithRenderer(loans, renderMultiLineString(property), "\u2014", isCombined);
export const createRateLoanGridElements = (loans, property, isCombined, bustedProperty) => createLoanGridElementsWithRenderer(loans, renderRate(property, bustedProperty), "\u2014", isCombined);
export const createPeriodLoanGridElements = (loans, property, isCombined) => createLoanGridElementsWithRenderer(loans, renderPeriod(property), "\u2014", isCombined);
export const createMoneyLoanGridElements = (loans, property, isCombined) => createLoanGridElementsWithRenderer(loans, renderMoney(property), "\u2014", isCombined);
export const createAbbreviatedMoneyLoanGridElements = (loans, property, isCombined, lessThan100kOverride = false) => createLoanGridElementsWithRenderer(loans, renderAbbreviatedMoney(property, lessThan100kOverride), "\u2014", isCombined);
export const createDSCRLoanGridElements = (loans, property, isCombined, bustedProperty) => createLoanGridElementsWithRenderer(loans, renderDSCR(property, bustedProperty), "\u2014", isCombined);
export const createHedgePremiumLoanGridElements = (loans, isCombined) => {
  loans.map((x, i) => {
    const hedgeType = _.get(x, "hedgeRequirementsRequiredHedgeValue", "\u2014");

    if (hedgeType == "Cap") {
      x.hedgePremium = numberFormatter.formatMoney(_.get(x, "hedgeRequirementsMarketCost", "\u2014"), 0);
    } else if (hedgeType == "Swap") {
      x.hedgePremium = numberFormatter.formatRate(_.get(x, "hedgeRequirementsSwapRate", "\u2014"), 2);
    }
  });

  return createLoanGridElementsWithRenderer(loans, renderString("hedgePremium"), "\u2014", isCombined);
};

export const createLoanGridElementsWithRenderer = (loans, renderer, placeholder, isCombined) => {
  let gridCol;
  if (loans.length <= 2) {
    gridCol = 4;
  } else if (loans.length >= 6) {
    gridCol = 1;
  } else if (loans.length >= 3) {
    gridCol = 2;
  } else {
    gridCol = 3;
  }

  return loans.map((x, i) => {
    if (x) {
      if (x.isExistingFinancing || x.originalLoan) {
        return (<AlterGridItem item xs={gridCol} key={`AlterGridItem${placeholder}+${i}`}><ContentText key={`ContentText${placeholder}+${i}`}>{renderer(x, placeholder)}</ContentText></AlterGridItem>);
      }

      return (<CustomGridItem item xs={gridCol} key={`CustomGridItem${placeholder}+${i}`}><ContentText key={`ContentText${placeholder}+${i}`}>{renderer(x, placeholder)}</ContentText></CustomGridItem>);
    }

    return null;
  });
};

